import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { leasesService } from "@/features/leases/services/leasesService"

export function useLeases(property_id?: string) {
  return useQuery({
    queryKey: property_id ? ["leases", { property_id }] : ["leases"],
    queryFn: () => leasesService.list(property_id),
  })
}

export function useLease(id: string) {
  return useQuery({
    queryKey: ["leases", id],
    queryFn: () => leasesService.get(id),
    enabled: !!id,
  })
}

export function useCreateLease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => leasesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leases"] })
      queryClient.invalidateQueries({ queryKey: ["properties"] })
      queryClient.invalidateQueries({ queryKey: ["tenants"] })
    },
  })
}

export function useUpdateLease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      leasesService.update(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["leases"] })
      queryClient.invalidateQueries({ queryKey: ["leases", id] })
      queryClient.invalidateQueries({ queryKey: ["properties"] })
      queryClient.invalidateQueries({ queryKey: ["rent-records"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useDeleteLease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => leasesService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leases"] })
      queryClient.invalidateQueries({ queryKey: ["properties"] })
    },
  })
}

export function useUnassignLease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => leasesService.unassign(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["leases"] })
      queryClient.invalidateQueries({ queryKey: ["leases", id] })
      queryClient.invalidateQueries({ queryKey: ["properties"] })
      queryClient.invalidateQueries({ queryKey: ["tenants"] })
      queryClient.invalidateQueries({ queryKey: ["rent-records"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useReactivateLease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => leasesService.reactivate(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["leases"] })
      queryClient.invalidateQueries({ queryKey: ["leases", id] })
      queryClient.invalidateQueries({ queryKey: ["properties"] })
      queryClient.invalidateQueries({ queryKey: ["tenants"] })
      queryClient.invalidateQueries({ queryKey: ["rent-records"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}
